import React, { useEffect, useState } from "react";
import apiClient from "../../../services/apiClient";
import { getHeaders } from "../../ApiHeaders/apiHeaders";
import { datadogRum } from "@datadog/browser-rum";
import Dropdown from "../DropDown"; // Reusable dropdown

interface Asset {
  asset_id: number;
  asset_name: string;
  iso_code?: string;
}

interface AssetDropDownProps {
  selectedAssetId: number | "";
  onAssetSelect: (asset: Asset | null) => void;
  selectedAsset: Asset | null;
  disabled: boolean;
  className?: string;
  onFocus?: () => void;
}

const AssetDropDown: React.FC<AssetDropDownProps> = ({
  selectedAssetId,
  onAssetSelect,
  disabled,
  className = "",
  onFocus,
}) => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        setLoading(true);
        const headers = await getHeaders();
        datadogRum.startView("AssetDropdown");

        const allAssets: Asset[] = [];
        let page = 1;
        let hasMorePages = true;
        while (hasMorePages) {
          const response = await apiClient.get(`/assets/?page=${page}&page_size=200`, {
            headers,
          });
          const fetchedAssets = response.data.response.data;
          const mapped = fetchedAssets.map((asset: any) => ({
            asset_id: asset.asset_id,
            asset_name: asset.asset_name,
            iso_code: asset.iso_code,
          }));
          allAssets.push(...mapped);
          hasMorePages = response.data.response.meta?.next_page != null;
          page++;
        }

        setAssets(allAssets);
        datadogRum.addAction("Fetched Assets Successfully", {
          count: allAssets.length,
        });
      } catch (err: any) {
        console.error("Failed to fetch assets", err);
        setError("Failed to fetch assets");
        datadogRum.addError("Failed to fetch assets", { error: err });
      } finally {
        setLoading(false);
      }
    };

    fetchAssets();
  }, []);

  const options = assets.map((asset) => ({
    value: asset.asset_id.toString(),
    label: asset.asset_name,
  }));

  const handleChange = (value: string) => {
    const assetId = Number(value);
    const selected = assets.find((a) => a.asset_id === assetId) || null;
    datadogRum.addAction("Asset Selected", { assetId: selected?.asset_id });
    onAssetSelect(selected);
  };

  return (
    <Dropdown
      options={options}
      selectedValue={selectedAssetId === "" ? "" : selectedAssetId.toString()}
      onValueChange={handleChange}
      disabled={disabled || loading || !!error}
      className={className}
      placeholder={
        loading ? "Loading assets..." : error ? error : "Select an asset"
      }
      onFocus={onFocus}
    />
  );
};

export default AssetDropDown;
